/**
 * Lightweight isometric paint helpers for OfficePreview.
 * Mirrors world-engine box/iso math without pulling in the full engine.
 */

export type IsoPoint = { x: number; y: number };
export type IsoPoly = Array<[number, number]>;

/** Grid → screen (same convention as world-engine). */
export function iso(
  ox: number,
  oy: number,
  tw: number,
  th: number,
  gx: number,
  gy: number,
): IsoPoint {
  return {
    x: ox + ((gx - gy) * tw) / 2,
    y: oy + ((gx + gy) * th) / 2,
  };
}

/** Painter's-algorithm depth (higher draws later / in front). */
export function depthKey(gx: number, gy: number, bias = 0): number {
  return gx + gy + bias;
}

/**
 * Nudge a #RRGGBB color by amt per channel (−255…255).
 * Invalid input is returned unchanged.
 */
export function shade(hex: string, amt: number): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.max(0, Math.min(255, ((n >> 16) & 255) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + amt));
  const b = Math.max(0, Math.min(255, (n & 255) + amt));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export function fillPoly(ctx: CanvasRenderingContext2D, pts: IsoPoly, fill: string): void {
  if (pts.length < 3) return;
  ctx.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const pt = pts[i];
    if (!pt) continue;
    if (i === 0) ctx.moveTo(pt[0], pt[1]);
    else ctx.lineTo(pt[0], pt[1]);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

export type DrawIsoBoxOpts = {
  left?: string;
  right?: string;
};

/**
 * World-engine-style iso box: left face, right face, then top.
 * (gx, gy) is the near-origin corner of the footprint; w/d in grid units; h in px.
 */
export function drawIsoBox(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  tw: number,
  th: number,
  gx: number,
  gy: number,
  w: number,
  d: number,
  h: number,
  top: string,
  opts: DrawIsoBoxOpts = {},
): { A: IsoPoint; B: IsoPoint; C: IsoPoint; D: IsoPoint } {
  const A = iso(ox, oy, tw, th, gx, gy);
  const B = iso(ox, oy, tw, th, gx + w, gy);
  const C = iso(ox, oy, tw, th, gx + w, gy + d);
  const D = iso(ox, oy, tw, th, gx, gy + d);
  const left = opts.left ?? shade(top, -28);
  const right = opts.right ?? shade(top, -14);

  fillPoly(
    ctx,
    [
      [D.x, D.y],
      [C.x, C.y],
      [C.x, C.y - h],
      [D.x, D.y - h],
    ],
    left,
  );
  fillPoly(
    ctx,
    [
      [C.x, C.y],
      [B.x, B.y],
      [B.x, B.y - h],
      [C.x, C.y - h],
    ],
    right,
  );
  fillPoly(
    ctx,
    [
      [A.x, A.y - h],
      [B.x, B.y - h],
      [C.x, C.y - h],
      [D.x, D.y - h],
    ],
    top,
  );
  return { A, B, C, D };
}

/**
 * Floor diamond for a [0,size)×[0,size) pod with rim + optional checker.
 */
export function drawIsoFloor(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  tw: number,
  th: number,
  size: number,
  rugColor: string,
  opts: { checker?: boolean } = {},
): void {
  const f0 = iso(ox, oy, tw, th, 0, 0);
  const f1 = iso(ox, oy, tw, th, size, 0);
  const f2 = iso(ox, oy, tw, th, size, size);
  const f3 = iso(ox, oy, tw, th, 0, size);

  // Soft drop shadow under the pod
  fillPoly(
    ctx,
    [
      [f0.x, f0.y + 4],
      [f1.x, f1.y + 4],
      [f2.x, f2.y + 4],
      [f3.x, f3.y + 4],
    ],
    "rgba(0,0,0,0.22)",
  );

  fillPoly(
    ctx,
    [
      [f0.x, f0.y],
      [f1.x, f1.y],
      [f2.x, f2.y],
      [f3.x, f3.y],
    ],
    rugColor,
  );

  // Rim (darker edge on far sides)
  ctx.strokeStyle = shade(rugColor, -35);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(f0.x, f0.y);
  ctx.lineTo(f1.x, f1.y);
  ctx.lineTo(f2.x, f2.y);
  ctx.lineTo(f3.x, f3.y);
  ctx.closePath();
  ctx.stroke();

  if (opts.checker) {
    const light = shade(rugColor, 12);
    const dark = shade(rugColor, -10);
    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        if ((x + y) % 2 !== 0) continue;
        const a = iso(ox, oy, tw, th, x, y);
        const b = iso(ox, oy, tw, th, x + 1, y);
        const c = iso(ox, oy, tw, th, x + 1, y + 1);
        const d = iso(ox, oy, tw, th, x, y + 1);
        fillPoly(
          ctx,
          [
            [a.x, a.y],
            [b.x, b.y],
            [c.x, c.y],
            [d.x, d.y],
          ],
          (x + y) % 4 === 0 ? light : dark,
        );
      }
    }
  }
}

export type DrawIsoWallsOpts = {
  wallH: number;
};

/**
 * Back walls of a size×size pod: N face (A→B) and W face (A→C).
 * N face is slightly lighter; W face is shaded.
 */
export function drawIsoWalls(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  tw: number,
  th: number,
  size: number,
  wallColor: string,
  opts: DrawIsoWallsOpts,
): { A: IsoPoint; B: IsoPoint; C: IsoPoint; wallH: number } {
  const { wallH } = opts;
  const A = iso(ox, oy, tw, th, 0, 0);
  const B = iso(ox, oy, tw, th, size, 0);
  const C = iso(ox, oy, tw, th, 0, size);
  const nFace = wallColor;
  const wFace = shade(wallColor, -22);
  const topLip = shade(wallColor, 18);

  // N wall (back-right from viewer when gy=0)
  fillPoly(
    ctx,
    [
      [A.x, A.y],
      [B.x, B.y],
      [B.x, B.y - wallH],
      [A.x, A.y - wallH],
    ],
    nFace,
  );
  // W wall (back-left)
  fillPoly(
    ctx,
    [
      [A.x, A.y],
      [C.x, C.y],
      [C.x, C.y - wallH],
      [A.x, A.y - wallH],
    ],
    wFace,
  );
  // Top lip along both walls
  ctx.strokeStyle = topLip;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(A.x, A.y - wallH);
  ctx.lineTo(B.x, B.y - wallH);
  ctx.moveTo(A.x, A.y - wallH);
  ctx.lineTo(C.x, C.y - wallH);
  ctx.stroke();

  // Baseboard
  const base = shade(wallColor, -40);
  ctx.strokeStyle = base;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(A.x, A.y);
  ctx.lineTo(B.x, B.y);
  ctx.moveTo(A.x, A.y);
  ctx.lineTo(C.x, C.y);
  ctx.stroke();

  return { A, B, C, wallH };
}

/**
 * Kinds with real Habbo art under public/assets/sprites/furniture
 * (file size > ~2KB). Everything else is a near-empty placeholder PNG.
 */
export const HERO_SPRITE_KINDS: ReadonlySet<string> = new Set([
  "desk",
  "chair",
  "plant",
  "lamp",
  "fox",
  "bookshelf",
  "neon",
  "arcade",
  "jukebox",
  "cooler",
  "couch",
  "sofa1",
  "reception",
  "coffeetable",
]);

export function isHeroSpriteKind(kind: string): boolean {
  return HERO_SPRITE_KINDS.has(kind);
}
